import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { WhyWorkWithUsSlider } from '@/components/WhyWorkWithUsSlider';
import { ValuesSlider } from '@/components/ValuesSlider';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <div className="min-h-screen" data-testid="about-page">
      <Helmet>
        <title>About Us - Trademark Venus | Experienced Trademark & Legal Services</title>
        <meta name="description" content="Learn about Trademark Venus's mission to protect businesses across the USA. Expert trademark and legal services with proven track record, fast processing, and transparent pricing." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-400 mb-4 block">
              About Us
            </span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Protecting Brands,{' '}
              <span className="bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
                Building Futures
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Trademark Venus is dedicated to providing comprehensive trademark and legal services that empower businesses across the United States to protect their most valuable assets—their brands.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY5NDYyMjIyfDA&ixlib=rb-4.1.0&q=85"
                alt="Our Team"
                className="rounded-3xl shadow-xl border border-slate-200 w-full h-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Our Mission
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                At Trademark Venus, we believe that every business deserves professional trademark protection regardless of size or industry. Our mission is to make trademark registration accessible, affordable, and stress-free for entrepreneurs and established businesses alike.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                We understand that your brand represents years of hard work, dedication, and vision. That's why we treat every trademark registration with the care and attention it deserves, ensuring your intellectual property receives the strongest possible protection.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                Our team of experienced professionals works tirelessly to navigate the complex world of trademark law, providing clear guidance and expert support every step of the way. We're not just service providers—we're your partners in building and protecting your brand's legacy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <ValuesSlider />
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-8 text-center">
                Our Approach
              </h2>
              
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Personalized Service
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We don't believe in one-size-fits-all solutions. Every client receives personalized attention and customized strategies tailored to their specific business needs and brand protection goals.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Transparent Communication
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We keep you informed throughout the entire process with clear, jargon-free communication. You'll always know the status of your application and what to expect next.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Comprehensive Protection
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    From initial trademark searches to registration and ongoing monitoring, we provide end-to-end services to ensure your brand receives complete protection at every stage.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Long-Term Partnership
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Our relationship doesn't end with registration. We're here to support your brand as it grows, providing ongoing guidance and protection services for years to come.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Why Work With Us
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Discover what sets us apart in trademark and legal services
            </p>
          </motion.div>

          <WhyWorkWithUsSlider />
        </div>
      </section>
    </div>
  );
}
